import { type ReactNode, useMemo, useState } from "react";
import { FlatList, Platform, type ViewStyle } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { format } from "date-fns";
import {
  Button,
  Chip,
  Icon,
  SkeletonGroup,
  SkeletonText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTableLayout,
  Text,
  View,
} from "@visioni/a3s-ui/components";
import type { ActivityItemViewModel } from "./activity-item";

export interface ActivityListProps {
  /** Pre-fetched activity items to display. Sorting is handled internally. */
  readonly items: readonly ActivityItemViewModel[];
  /** Whether the list is currently loading (initial fetch). */
  readonly isLoading?: boolean;
  /** Whether a fetch error occurred. */
  readonly error?: boolean;
  /** Called when the camera label link is pressed. */
  readonly onCameraPress?: (item: ActivityItemViewModel) => void;
  /** Total count of matching records (for the "Showing X of Y" indicator). */
  readonly totalCount?: number | undefined;
  /** Whether more items can be loaded. */
  readonly hasNextPage?: boolean;
  /** Whether the next page is currently being fetched. */
  readonly isFetchingNextPage?: boolean;
  /** Load the next page of results. */
  readonly onLoadMore?: () => void;
  /** Render function for expandable detail content below a row. */
  readonly renderDetails?: (item: ActivityItemViewModel) => ReactNode;
}

/** Shared by the header and every row so the columns line up. */
const COLUMNS = {
  summary: { flex: 3, minWidth: 200 },
  scope: { flex: 2, minWidth: 140 },
  user: { flex: 2, minWidth: 150 },
  time: { width: 110 },
  expand: { width: 48 },
} as const;

/** Below this the columns don't fit, so each row stacks its cells instead. */
const TABLE_MIN_WIDTH = 200 + 140 + 150 + 110 + 48;

/** The audit log: one row per activity, newest first; "Load more" appends the next page. */
export function ActivityList({
  items,
  isLoading,
  error,
  onCameraPress,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  renderDetails,
}: ActivityListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [items],
  );

  // FlatList only re-renders rows when `data` or `extraData` change.
  const extraData = useMemo(
    () => ({ expandedId, onCameraPress, renderDetails }),
    [expandedId, onCameraPress, renderDetails],
  );

  if (isLoading && items.length === 0) return <ActivityListSkeleton />;

  if (error) {
    return (
      <View flex={1} bg="background" alignItems="center" justifyContent="center" px="4xl">
        <Text textStyle="sm/medium" color="danger">
          Unable to load audit logs. Please try again later.
        </Text>
      </View>
    );
  }

  if (!isLoading && items.length === 0 && !hasNextPage) {
    return (
      <View flex={1} bg="background" alignItems="center" justifyContent="center">
        <Text textStyle="sm/regular" color="textMuted">
          No audit logs found
        </Text>
      </View>
    );
  }

  const showCount = totalCount != null && items.length > 0;
  const showLoadMore = hasNextPage === true && onLoadMore !== undefined;

  return (
    <View style={styles.page}>
      <TableContainer horizontalScroll stackBelow={TABLE_MIN_WIDTH} style={styles.container}>
        <Table minWidth={TABLE_MIN_WIDTH} accessibilityLabel="Audit log">
          <ActivityListHeader />
          <TableBody>
            <FlatList
              data={sortedItems}
              extraData={extraData}
              keyExtractor={(item) => item.id}
              style={Platform.OS === "web" ? webListStyle : styles.list}
              renderItem={({ item }) => (
                <ActivityRow
                  item={item}
                  expanded={item.id === expandedId}
                  onToggle={() => {
                    setExpandedId((current) => (current === item.id ? null : item.id));
                  }}
                  {...(onCameraPress ? { onCameraPress } : {})}
                  {...(renderDetails ? { renderDetails } : {})}
                />
              )}
              ListFooterComponent={
                showCount || showLoadMore ?
                  <View alignItems="center" py="xl" gap="lg">
                    {showCount && (
                      <Text textStyle="xs/regular" color="textMuted">
                        Showing {items.length} of {totalCount} results
                      </Text>
                    )}
                    {showLoadMore && (
                      <Button
                        label="Load more"
                        variant="secondary"
                        variantColor="grey"
                        size="sm"
                        onPress={onLoadMore}
                        loading={isFetchingNextPage ?? false}
                        loadingText="Loading…"
                      />
                    )}
                  </View>
                : null
              }
            />
          </TableBody>
        </Table>
      </TableContainer>
    </View>
  );
}

function ActivityListHeader() {
  return (
    <TableHead scrollbarGutter>
      <TableRow>
        <TableCell {...COLUMNS.summary}>Summary</TableCell>
        <TableCell {...COLUMNS.scope}>Scope</TableCell>
        <TableCell {...COLUMNS.user}>User</TableCell>
        <TableCell {...COLUMNS.time} align="right">
          Time
        </TableCell>
        <TableCell {...COLUMNS.expand} />
      </TableRow>
    </TableHead>
  );
}

/** Rows in the loading skeleton: roughly one screen. */
const SKELETON_KEYS = Array.from({ length: 12 }, (_, index) => `skeleton-${String(index)}`);

/** The audit log's layout with placeholder rows, shown while the first page loads. */
function ActivityListSkeleton() {
  return (
    <View style={styles.page}>
      <TableContainer horizontalScroll stackBelow={TABLE_MIN_WIDTH} style={styles.container}>
        <Table minWidth={TABLE_MIN_WIDTH} accessibilityLabel="Loading audit log">
          <ActivityListHeader />
          <TableBody>
            <SkeletonGroup>
              {SKELETON_KEYS.map((key) => (
                <TableRow key={key}>
                  <TableCell {...COLUMNS.summary}>
                    <SkeletonText lines={1} textStyle="sm/regular" width={160} />
                  </TableCell>
                  <TableCell {...COLUMNS.scope}>
                    <SkeletonText lines={1} textStyle="sm/regular" width={100} />
                  </TableCell>
                  <TableCell {...COLUMNS.user}>
                    <SkeletonText lines={1} textStyle="sm/regular" width={110} />
                  </TableCell>
                  <TableCell {...COLUMNS.time} align="right">
                    <SkeletonText lines={2} textStyle="xs/regular" width={64} />
                  </TableCell>
                  <TableCell {...COLUMNS.expand} />
                </TableRow>
              ))}
            </SkeletonGroup>
          </TableBody>
        </Table>
      </TableContainer>
    </View>
  );
}

interface ActivityRowProps {
  item: ActivityItemViewModel;
  expanded: boolean;
  onToggle: () => void;
  onCameraPress?: (item: ActivityItemViewModel) => void;
  renderDetails?: (item: ActivityItemViewModel) => ReactNode;
}

function ActivityRow({ item, expanded, onToggle, onCameraPress, renderDetails }: ActivityRowProps) {
  const stacked = useTableLayout() === "stacked";
  const expandable = item.hasDetails && renderDetails !== undefined;
  const createdAt = new Date(item.createdAt);
  // `muted`, the default, is the card's own colour in dark mode.
  const cameraChip =
    item.cameraLabel === null ? null
    : onCameraPress && item.cameraKey ?
      <Chip label={item.cameraLabel} color="borderCard" onPress={() => onCameraPress(item)} trailingIcon="arrowRight" />
    : <Chip label={item.cameraLabel} color="borderCard" />;
  const chevron = expandable ? <ExpandChevron expanded={expanded} /> : null;

  return (
    <TableRow
      hover
      {...(expandable ?
        {
          onPress: onToggle,
          expanded,
          expandedContent: <View style={styles.detail}>{renderDetails(item)}</View>,
        }
      : {})}
      accessibilityLabel={`${item.summary}, ${item.cameraLabel ?? "no camera"}, ${item.userEmail ?? "unknown user"}, ${format(createdAt, "d MMM yy h:mma")}`}
    >
      {stacked ?
        <>
          <TableCell>
            <View flexDirection="row" alignItems="center" gap="md" alignSelf="stretch">
              <View flex={1} style={styles.shrinkable}>
                <Text textStyle="sm/medium" color="text">
                  {item.summary}
                </Text>
              </View>
              {chevron}
            </View>
          </TableCell>
          {cameraChip && (
            <TableCell>
              <View flexDirection="row">{cameraChip}</View>
            </TableCell>
          )}
          <TableCell>
            <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
              {[item.userEmail, format(createdAt, "d MMM yy h:mma")].filter((part) => part !== null).join(" · ")}
            </Text>
          </TableCell>
        </>
      : <>
          <TableCell {...COLUMNS.summary} lineClamp={2}>
            {item.summary}
          </TableCell>
          <TableCell {...COLUMNS.scope}>
            {cameraChip ?? (
              <Text textStyle="sm/regular" color="textMuted">
                —
              </Text>
            )}
          </TableCell>
          <TableCell {...COLUMNS.user} noWrap>
            {item.userEmail ?? "—"}
          </TableCell>
          <TableCell {...COLUMNS.time} align="right">
            <Text textStyle="sm/regular" color="text" textAlign="right">
              {format(createdAt, "h:mma")}
            </Text>
            <Text textStyle="xs/regular" color="textMuted" textAlign="right">
              {format(createdAt, "d MMM yy")}
            </Text>
          </TableCell>
          <TableCell {...COLUMNS.expand} align="center">
            {chevron}
          </TableCell>
        </>
      }
    </TableRow>
  );
}

function ExpandChevron({ expanded }: { readonly expanded: boolean }) {
  const { theme } = useUnistyles();
  return <Icon name={expanded ? "chevronUp" : "chevronDown"} size={14} color={theme.colors.textMuted} />;
}

// Also reserves the scrollbar's width on web, matching `TableHead scrollbarGutter`,
// so the columns line up when a classic scrollbar shows. `scrollbar-gutter` isn't
// a native style, so it stays out of the unistyles sheet.
const webListStyle = Object.assign({ flex: 1 } satisfies ViewStyle, { scrollbarGutter: "stable" });

const styles = StyleSheet.create((theme) => ({
  page: {
    flex: 1,
    backgroundColor: theme.colors.background,
    paddingHorizontal: theme.spacing["4xl"],
    paddingTop: theme.spacing["4xl"],
    paddingBottom: theme.spacing["4xl"],
  },
  container: {
    flex: 1,
  },
  list: {
    flex: 1,
  },
  shrinkable: {
    minWidth: 0,
  },
  // The expanded area sits straight under the row's cells, so it brings its
  // own top spacing.
  detail: {
    paddingTop: theme.spacing.xl,
    gap: theme.spacing.lg,
  },
}));
